import { createSlice, current, PayloadAction } from "@reduxjs/toolkit";
import { ColorSchemesAPI } from "../../server/types/index.js";
import {
  ColorScheme,
  ColorSchemeCategory,
  ColorSchemePutData,
  ColorSchemeState,
  ThunkAction,
} from "../types/index.js";
import { ColorSchemeNotFoundError, fetch } from "../utils/utils.js";
import { showAlert } from "./alerts.js";
import { createAppAsyncThunk } from "./utils.js";

const sliceName = "colorSchemes";

export const defaultColorScheme: ColorScheme = {
  main: {
    background: "#ffffff",
    title: "#9b4341",
    text: "#5e6d82",
    separator: "#b79f73",
  },
  primary: { background: "#9b4341", text: "#ffffff" },
  secondary: { background: "#b79f73", text: "#ffffff" },
};

const initialState: ColorSchemeState = {
  defaultColorScheme,
  schemes: {},
  prevState: {},
};

export const getColorSchemes = createAppAsyncThunk(
  `${sliceName}/getColorSchemes`,
  (siteId: string, { dispatch }) =>
    fetch({ dispatch })
      .get(`sites/${siteId}/color-schemes`)
      .json<ColorSchemesAPI>()
      .catch((error) => {
        dispatch(showAlert("Die Farbschemen konnten nicht geladen werden!"));
        throw error;
      }),
  {
    condition: (_: string, { getState }) => {
      const loadState = getState().loadStates.colorSchemes;
      return loadState === "unloaded" || loadState === undefined;
    },
  },
);

export const putColorScheme = createAppAsyncThunk<
  void,
  { siteId?: string; colorSchemeId: string },
  { pendingMeta: { scheme: ColorScheme | undefined } }
>(
  `${sliceName}/putColorScheme`,
  async ({ siteId, colorSchemeId }, { dispatch, getState }) => {
    const scheme = getState().colorSchemes.schemes[colorSchemeId];
    if (!scheme) return;

    const colorSchemePutData: ColorSchemePutData = { scheme };

    try {
      await fetch({ dispatch })
        .put(
          colorSchemePutData,
          `sites/${siteId}/color-schemes/${colorSchemeId}`,
        )
        .res();
    } catch (error) {
      dispatch(
        showAlert("Das Aktualisieren des Farbschemen ist fehlgeschlagen!"),
      );
      throw error;
    }
  },
  {
    dispatchConditionRejection: true,
    condition: ({ colorSchemeId }: { colorSchemeId: string }, { getState }) =>
      !!getState().colorSchemes.schemes[colorSchemeId],
    getPendingMeta: ({ arg: { colorSchemeId } }, { getState }) => ({
      scheme: getState().colorSchemes.schemes[colorSchemeId],
    }),
  },
);

export const deleteColorScheme = createAppAsyncThunk<
  void,
  { siteId?: string; colorSchemeId: string },
  { rejectValue: ColorSchemesAPI }
>(
  `${sliceName}/deleteColorScheme`,
  async ({ siteId, colorSchemeId }, { dispatch }) => {
    try {
      await fetch({ dispatch })
        .delete(`sites/${siteId}/color-schemes/${colorSchemeId}`)
        .res();
    } catch (error) {
      dispatch(
        showAlert("Beim Löschen des Farbschemen ist ein Fehler aufgetreten!"),
      );
      throw error;
    }
  },
);

export const duplicateColorScheme = (
  siteId: string,
  colorSchemeId: string,
): ThunkAction<void> => {
  const newId = crypto.randomUUID();

  return (dispatch) => {
    dispatch(duplicateColorSchemeStart({ schemeId: colorSchemeId, newId }));
    dispatch(putColorScheme({ siteId, colorSchemeId: newId }));
  };
};

const slice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    changeColor: (
      state,
      {
        payload: { schemeId, categoryId, colorType, color },
      }: PayloadAction<{
        schemeId: string;
        categoryId: ColorSchemeCategory;
        colorType: keyof ColorScheme[ColorSchemeCategory];
        color: string;
      }>,
    ) => {
      const scheme = state.schemes[schemeId];
      if (!scheme) throw new ColorSchemeNotFoundError(schemeId);
      scheme[categoryId][colorType] = color;
    },

    duplicateColorSchemeStart: (
      state,
      {
        payload: { schemeId, newId },
      }: PayloadAction<{ schemeId: string; newId: string }>,
    ) => {
      const newScheme = state.schemes[schemeId];
      if (!newScheme) throw new ColorSchemeNotFoundError(schemeId);

      state.schemes[newId] = newScheme;
    },

    setColorScheme: (
      state,
      action: PayloadAction<{
        colorSchemeId: string;
        scheme: ColorScheme;
        onlyIfExists?: boolean;
      }>,
    ) => {
      const current = state.schemes[action.payload.colorSchemeId];
      if (!current && action.payload.onlyIfExists) {
        return;
      }
      state.schemes[action.payload.colorSchemeId] = action.payload.scheme;
    },

    removeColorScheme: (
      state,
      { payload: { colorSchemeId } }: PayloadAction<{ colorSchemeId: string }>,
    ) => {
      delete state.schemes[colorSchemeId];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getColorSchemes.fulfilled, (state, action) => ({
      ...state,
      ...action.payload,
    }));

    builder.addCase(putColorScheme.pending, (state, action) => {
      if (!action.meta.scheme) return;
      state.schemes[action.meta.arg.colorSchemeId] = action.meta.scheme;
    });

    builder.addCase(putColorScheme.rejected, (_state, action) => {
      if (action.meta.condition) {
        throw new ColorSchemeNotFoundError(action.meta.arg.colorSchemeId);
      }
    });

    builder.addCase(deleteColorScheme.pending, (state, action) => {
      const { prevState, ...snapshot } = current(state);
      state.prevState[action.meta.requestId] = { ...snapshot, prevState: {} };

      delete state.schemes[action.meta.arg.colorSchemeId];
    });

    builder.addCase(deleteColorScheme.fulfilled, (state, action) => {
      delete state.prevState[action.meta.requestId];
    });

    builder.addCase(deleteColorScheme.rejected, (state, action) => {
      const { requestId } = action.meta;
      const prevState = state.prevState[requestId];
      if (!prevState) return;

      delete state.prevState[requestId];
      state.defaultColorScheme = prevState.defaultColorScheme;
      state.schemes = prevState.schemes;
    });
  },
});

export const {
  changeColor,
  duplicateColorSchemeStart,
  setColorScheme,
  removeColorScheme,
} = slice.actions;

export default slice.reducer;
